
import React, { useState, useEffect } from 'react'
import { useTasks } from '@/hooks/useTasks'
import { useUserInspirations } from '@/hooks/useUserInspirations'
import { supabase } from '@/integrations/supabase/client'
import DiscoverTasksList from './discover/DiscoverTasksList'
import InspoTasksList from './discover/InspoTasksList'

interface DiscoverTasksViewProps {
  selectedDomain?: string
  selectedModule?: string
}

const DiscoverTasksView: React.FC<DiscoverTasksViewProps> = ({ 
  selectedDomain = '', 
  selectedModule = '' 
}) => {
  const { getDiscoverTasks, addTaskToMyTasks, loading } = useTasks()
  const { inspirations, addToMyList } = useUserInspirations()
  const [taskInspirations, setTaskInspirations] = useState<any[]>([])
  
  const discoverTasks = getDiscoverTasks()
  
  // Apply domain and module filtering to discover tasks only
  const filteredDiscoverTasks = discoverTasks.filter(task => {
    if (selectedDomain && task.domain !== selectedDomain) return false
    if (selectedModule && task.module !== selectedModule) return false
    return true
  })

  // Fetch task data for quiz-based inspirations only
  useEffect(() => {
    const fetchTaskInspirations = async () => {
      // Filter to only include inspirations that came from quiz completions
      const taskInspirs = inspirations.filter(insp => {
        const hasTaskId = !!insp.task_id
        const notAddedToList = !insp.added_to_my_list
        const hasQuizSource = !!insp.source_quiz_id
        
        return hasTaskId && notAddedToList && hasQuizSource
      })
      
      if (taskInspirs.length > 0) {
        const { data: adminTasksData } = await supabase
          .from('admin_tasks')
          .select('*')
          .in('id', taskInspirs.map(insp => insp.task_id))

        let inspoTasks = taskInspirs.map(insp => {
          const taskData = adminTasksData?.find(task => task.id === insp.task_id)
          return { ...insp, taskData }
        }).filter(insp => insp.taskData)

        // Apply filtering to inspiration tasks as well
        inspoTasks = inspoTasks.filter(insp => {
          if (selectedDomain && insp.taskData.domain !== selectedDomain) return false
          if (selectedModule && insp.taskData.module !== selectedModule) return false
          return true
        })

        setTaskInspirations(inspoTasks)
      } else {
        setTaskInspirations([])
      }
    }

    fetchTaskInspirations()
  }, [inspirations, selectedDomain, selectedModule])

  const handleAddTask = async (taskId: string, taskSource: 'admin' | 'school') => {
    try {
      const success = await addTaskToMyTasks(taskId, taskSource)
      if (!success) {
        console.error('Failed to add task to My Tasks')
      }
    } catch (error) {
      console.error('Error in handleAddTask:', error)
    }
  }

  const handleAddInspirationTask = async (inspiration: any) => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) return

      // Add to custom tasks
      await supabase.from('custom_tasks').insert({
        user_id: session.user.id,
        title: inspiration.taskData.title,
        description: inspiration.taskData.description,
        module: inspiration.taskData.module,
        domain: inspiration.taskData.domain,
        status: 'active'
      })

      // Mark inspiration as added and immediately remove from local state
      await addToMyList(inspiration.id)
      
      // Immediately remove from local state for instant UI update
      setTaskInspirations(prev => prev.filter(item => item.id !== inspiration.id))

    } catch (error) {
      console.error('Error adding inspiration task:', error)
    }
  }

  if (loading) {
    return <div className="text-center py-8">Loading tasks...</div>
  }

  return (
    <div className="grid grid-cols-2 gap-6">
      <DiscoverTasksList 
        tasks={filteredDiscoverTasks}
        onAddTask={handleAddTask}
      />
      <InspoTasksList 
        inspirations={taskInspirations}
        onAddInspirationTask={handleAddInspirationTask}
      />
    </div>
  )
}

export default DiscoverTasksView
